/**
 * Global scroll behavior helpers.
 *
 * The app uses a <main overflow-y-auto> element as its scroll container
 * (see Layout.tsx).  Plain `window.scrollTo` does NOT work because
 * the window itself is `h-screen overflow-hidden`.
 *
 * ── Wizard / stepper step changes ──
 *   import { useScrollOnStepChange } from '@/hooks/useScrollBehavior';
 *   useScrollOnStepChange(activeStep);
 *
 * ── Imperative one-off scroll ──
 *   import { scrollMainToTop } from '@/hooks/useScrollBehavior';
 *   scrollMainToTop();
 */

import { useEffect, useRef } from 'react';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

/** Returns the `<main>` scroll container rendered by Layout. */
function getMainScrollContainer(): HTMLElement | null {
  return document.querySelector('main');
}

/**
 * Scroll the main content area to top.
 * Falls back to `window.scrollTo` for pages rendered outside Layout
 * (login, registration, etc.).
 */
export function scrollMainToTop(behavior: ScrollBehavior = 'smooth'): void {
  const main = getMainScrollContainer();
  if (main) {
    main.scrollTo({ top: 0, behavior });
  } else {
    window.scrollTo({ top: 0, behavior });
  }
}

/* ------------------------------------------------------------------ */
/*  Hooks                                                             */
/* ------------------------------------------------------------------ */

/**
 * Scrolls the main content area to top whenever `step` changes.
 * Drop into any wizard / stepper component:
 *
 *   const [activeStep, setActiveStep] = useState(0);
 *   useScrollOnStepChange(activeStep);
 *
 * Pass `disabled: true` to skip scrolling (e.g. when the wizard is
 * rendered inside a Dialog and scrolling <main> would be wrong).
 */
export function useScrollOnStepChange(
  step: number,
  { disabled = false }: { disabled?: boolean } = {},
): void {
  const prev = useRef(step);
  useEffect(() => {
    if (!disabled && prev.current !== step) {
      // Use 'instant' so the scroll isn't interrupted by layout shifts
      // from the new step content rendering. Wrap in rAF to ensure the
      // new step's DOM is painted before we scroll.
      requestAnimationFrame(() => scrollMainToTop('instant'));
    }
    prev.current = step;
  }, [step, disabled]);
}
